import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { ManagedStocksApi } from "apis/ManagedStocksApi";
import { ManagedSupplierDealsApi } from "apis/ManagedSupplierDealsApi";
import { StoresApi } from "apis/StoresApi";
import { CommonColumnProperties, CustomColumns, FormInput, ImportBy, ImportWizard, ImportWizardRef, ImportWizardSetting, Modal, Spinner, Title } from "components";
import { AgGridToolbar, AgGridToolbarRef } from "components/AgGrid/AgGridToolbar";
import { ValidationState } from "components/ImportWizard/ImportWizardTypes";
import { runBatchWorkers } from "components/WebWorkers/ZWebWorker";
import { ZButton } from "components/ZButton";
import { ZMultiSelectDropdown } from "components/ZMultiSelectDropdown";
import { ZTextInput } from "components/ZTextInput";
import * as datefns from "date-fns";
import { ZIconType } from "helpers/utility/iconMap";
import { useProductFilter, useToggle } from "hooks";
import { ManagedSupplierDealsStockView, ManagedSupplierDealsTemplateView } from "models/dbModels/managedSupplierDeals/ManagedSupplierDealsTemplate";
import { ManagedSupplier } from "models/dbModels/managedSuppliers/ManagedSupplier";
import { StoreExtended } from "models/dbModels/stores/Store";
import { SupplierNameMappingView } from "models/dbModels/supplierNameMappings/SupplierNameMappingView";
import { ManagedStocksPopup } from "popups/ManagedStocks/ManagedStocks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Form, Offcanvas, OffcanvasBody, OffcanvasHeader, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getSupplierNameMappings } from "redux/slices/SupplierNameMappings";
import { getSuppliers } from "redux/slices/Suppliers";
import { showToast } from "redux/slices/Toasts";
import { RootState } from "redux/store";
import { SupplierDealImportWorkerData, SupplierDealImportWorkerResponseData } from "./supplierDealImportWorker";
import { ZSingleSelectDropdown } from "components/ZSingleSelectDropdown";
import { ZDateTimePicker } from "components/ZDateTimePicker";
import { ZNumberInput } from "components/ZNumberInput";
import { ZLabel } from "components/ZLabel";
import { AgGridCustomColumn } from "components/AgGrid/AgCustomColumnTemplate";
import { SearchState } from "components/AgGrid/FilterComponents/Search";
import { FilterState } from "components/AgGrid/FilterComponents/Filters";
import { ZTooltip } from "components/ZTooltip";
import BaseCellRenderer from "components/AgGrid/CellRenderers/BaseCellRenderer";
import { ZIcon } from "components/ZIcon";
import { BaseColDefParams } from "ag-grid-community/dist/lib/entities/colDef";
import { v4 as uuidv4 } from 'uuid';
import { Modal as BSModal } from "react-bootstrap";


export enum DiscountTypes {
	Fixed = "C",
	Percentage = "P",
}


interface CurrentModel extends ManagedSupplierDealsStockView {
	hasError?: { [key in keyof ManagedSupplierDealsStockView]?: boolean };
	customId?: string;
}

export function NewSupplierDeal() {
	const getProductFilterOptions = useProductFilter();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [showPopup, setShowPopup] = useState<boolean>(false);
	const user = useAppSelector((state: RootState) => state.logedinUser);
	const importWizardRef = useRef<ImportWizardRef>(null);
	const [showImport, setShowImport] = useState<boolean>(false);
	const [showAdd, setShowAdd] = useState<boolean>(false);
	const stocks = useAppSelector((state: RootState) => state.stocks);
	const { modelId } = useParams();

	const [stores, setStores] = useState<StoreExtended[]>([]);
	const [suppliers, setSuppliers] = useState<ManagedSupplier[]>([]);
	const [supplierNameMappings, setSupplierNameMappings] = useState<SupplierNameMappingView[]>([]);

	const [isNew, setIsNew] = useState<boolean>(true);
	const [isValidated, setValidated] = useToggle();
	const form = useRef<HTMLFormElement>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [saving, setSaving] = useState<boolean>(false);
	const [showConfirm, setShowConfirm] = useState<boolean>(false);
	const toolbarRef = useRef<AgGridToolbarRef>(null);
	const stockTableRef = useRef<AgGridReact>(null);
	const [selected, setSelected] = useState<number[]>([]);
	const [globalDiscountType, setGlobalDiscountType] = useState<string>("Fixed");
	const [showBulkEdit, setShowBulkEdit] = useState<boolean>(false);
	const [totalRows, setTotalRows] = useState<number>(0);
	const [hasErrors, setHasErrors] = useState<boolean>(false);
	const [bulkOptions, setBulkOptions] = useState<{
		supplierId: number | null;
		startDate: string | null;
		endDate: string | null;
		qty: number | null;
		value: number | null;
		dealType: "C" | "P" | null;
		notes: string | null;
	}>({
		supplierId: null,
		startDate: null,
		endDate: null,
		qty: null,
		value: null,
		dealType: null,
		notes: null,
	});

	const [supplierDeal, setSupplierDeal] = useState<ManagedSupplierDealsTemplateView>({
		id: 0,
		name: "",
		stocks: [],
		stores: [],
		stockCount: 0,
		storeCount: 0,
		notes: "",
	});

	const [supplierDealStocks, setSupplierDealStocks] = useState<CurrentModel[]>([]);

	const gridSearch = useRef<{ searchState?: SearchState; filterState?: FilterState }>({});

	const blankSupplier: ManagedSupplier = {
		name: "",
		id: 0,
		isSystem: false,
		active: false,
		contactName: null,
		address: null,
		suburb: null,
		state: null,
		postcode: null,
		phoneNumber: null,
		faxNumber: null,
		lock: false,
		modelId: "",
		systemCode: null
	};

	const canEdit = (modelId && user.permissions.supplierDeals.canUpdate) ||
					(!modelId && user.permissions.supplierDeals.canCreate);

	useEffect(() => {
		if (modelId && !isNaN(Number.parseInt(modelId))) {
			setLoading(true);
			//Load the existing Stock deal
			ManagedSupplierDealsApi.Search({ ids: [Number.parseInt(modelId)], loadDetails: ["Stores"] })
				.then(async (res) => {
					if (res.data.data.length === 1) {
						setSupplierDeal(res.data.data[0]);

						const fetchStocks = async () => {
							const stocks: ManagedSupplierDealsStockView[] = [];
							let hasStock = true;
							let offset = 0;
						
							while (hasStock) {
								const stockRes = await ManagedSupplierDealsApi.SearchStock({ supplierDealIds: [res.data.data[0].id], limit: 500, offset: offset });
								if (stockRes.data.data.length === 0 || stockRes.data.data.length < 500) {
									hasStock = false;
								} 
								
								stocks.push(...stockRes.data.data.map((s) => ({ ...s, customId: uuidv4(), hasError: {} })));
								offset += 500;
							}						
							return stocks;
						};
						
						const stocks = await fetchStocks();

						setTotalRows(stocks.length);						
						setSupplierDealStocks(stocks);
						setGlobalDiscountType(res.data.data.length > 0 && stocks.length > 0 && stocks[0].dealType === "C" ? "Fixed" : "Percentage");
						setIsNew(false);
						setLoading(false);
					} else {
						navigate("/app/supplier-stock-deals/new");
						setLoading(false);
					}
				})
				.catch(() => {
					navigate("/app/supplier-stock-deals/new");
					setLoading(false);
				});
		}
	}, [modelId]);

	//Load the models into states
	useEffect(() => {
		dispatch(getSuppliers()).then((res) => {
			setSuppliers(res.data);
		});

		dispatch(getSupplierNameMappings()).then((res) => {
			setSupplierNameMappings(res.data);
		});

		StoresApi.Search({}).then((res) => {
			setStores(res.data.data);
		});
	}, []);


	const stockColumns = useMemo(() => {
		if (suppliers.length === 0) return [] as ColDef<CurrentModel>[];

		var columns: ColDef<CurrentModel>[] = [
			{
				headerName: "Stock Name",
				sortable: true,
				field: "name",
				minWidth: 300,
				flex: 1,
				sort: "asc",
				sortIndex: 0,
				filter: "agTextColumnFilter",
				checkboxSelection: true,
				headerCheckboxSelection: true,
				headerCheckboxSelectionFilteredOnly: true,
				headerCheckboxSelectionCurrentPageOnly: false,
				cellRenderer: "baseCellRenderer",
			},
			// {
			// 	headerName: "Errors",
			// 	field: "hasError",
			// 	valueGetter: (params) => JSON.stringify(params.data?.hasError)
			// },
			{
				headerName: "Supplier",
				field: "supplierId",
				sortable: true,
				minWidth: 200,
				flex: 1,
				cellEditor: "agSelectCellEditor",
				cellEditorParams: {
					values: [blankSupplier, ...suppliers].map((s) => s.name),
				},
				valueGetter: (params) => {
					const supplier = suppliers.find((s) => s.id === params.data?.supplierId);
					return supplier ? supplier.name : "";
				},
				valueSetter: (params) => {
					params.data.supplierId = findSupplierId(params.newValue);
					params.data.hasError!["supplierId"] = params.data.supplierId == null;
					return true;
				},
				editable: canEdit ? true : false,
				cellRenderer: "baseCellRenderer",
				cellRendererParams: (params: BaseColDefParams<CurrentModel>) => {
					if (params.data.hasError!["supplierId"] === true || params.data.hasError!["name"] === true) {
						const errorText: string[] = [];
						if (params.data.hasError!["supplierId"]) {
							errorText.push("Supplier is required");
						}
						if (params.data.hasError!["name"]) {
							errorText.push("Duplicate line");
						}
						return {
							decorators: [
									{
										decorator: <ZTooltip text={errorText.join(", ")}><ZIcon icon={ZIconType.Alert} variant="danger" /></ZTooltip>,
										position: "start",
									},
								],
							};
					}				
				},
			},
			{
				headerName: "Cost Ex",
				field: "costEx",
				sortable: true,
				minWidth: 150,
				initialWidth: 150,
				cellRenderer: "DecimalCellRenderer"
			},
			{
				headerName: "MOQ",
				field: "qty",
				sortable: true,
				minWidth: 150,
				initialWidth: 150,
				valueSetter: (params) => {
					params.data.qty = params.newValue;
					params.data.hasError!["qty"] = (params.data.qty < 0 || isNaN(params.data.qty) || params.data.qty == null);
					return true;
				},
				cellRenderer: "baseCellRenderer",
				cellRendererParams: (params: BaseColDefParams<CurrentModel>) => {
					if (params.data.hasError!["qty"] === true || params.data.hasError!["name"] === true) {
						const errorText: string[] = [];
						if (params.data.hasError!["qty"]) {
							errorText.push("Invalid MOQ");
						}
						if (params.data.hasError!["name"]) {
							errorText.push("Duplicate line");
						}
						return {
							decorators: [
									{
										decorator: <ZTooltip text={errorText.join(", ")}><ZIcon icon={ZIconType.Alert} variant="danger" /></ZTooltip>,
										position: "start",
									},
								],
							};
					}				
				},
				editable: canEdit ? true : false,
				sort: "asc",
				sortIndex: 1,				
				headerTooltip: "Minimum Order Quantity",
			},
			{
				headerName: globalDiscountType === "Fixed" ? "Dollar Discount" : "Percent Discount",
				field: "value",
				sortable: true,
				minWidth: 150,
				initialWidth: 150,
				cellRendererSelector: (params) =>  params.data?.dealType === "C" ? {component: "PriceCellRenderer"} : {component: "DecimalCellRenderer"},
				editable: canEdit ? true : false,
				valueSetter: (params) => {
					if (params.newValue == null) {
						return false;
					}
	
					if (isNaN(params.newValue)) {
						return false;
					}
					if (parseFloat(params.newValue) < 0) {
						return false;
					}
					params.data.value = parseFloat(params.newValue);
					return true;
				},
			},
			{
				headerName: "NIS Cost",
				field: "expectedCost",
				sortable: true,
				minWidth: 150,
				initialWidth: 150,
				cellRenderer: "DecimalCellRenderer",
				editable: canEdit ? true : false,
				valueSetter: (params) => {
					if (params.newValue == null) {
						return true;
					}
	
					if (isNaN(params.newValue)) {
						return false;
					}
					if (parseFloat(params.newValue) < 0) {
						return false;
					}
					params.data.expectedCost = parseFloat(params.newValue);
					return true;
				},
				headerTooltip: "Net into Store cost (Expected Cost)"
			},
			{
				headerName: "Notes",
				field: "notes",
				sortable: true,
				minWidth: 200,
				editable: canEdit ? true : false,
			},
			{
				headerName: "Start",
				field: "startDate",
				sortable: true,
				minWidth: 150,
				initialWidth: 150,
				cellEditor: "agDateCellEditor",
				valueSetter: (params) => {
					params.data.startDate = params.newValue ? datefns.format(params.newValue, "yyyy-MM-dd") : null;
					return true;
				},
				valueGetter: (params) => (params.data?.startDate == null ? null : new Date(params.data.startDate)),
				valueFormatter: (params) => (params.value ? datefns.format(new Date(params.value), "dd/MM/yyyy") : ""),
				editable: canEdit ? true : false,
				
				cellRenderer: "baseCellRenderer",
				cellEditorParams: {
					
				},
				cellRendererParams: (params: BaseColDefParams<CurrentModel>) => {
					if (params.data.hasError!["startDate"] === true) {
						return {
							decorators: [
									{
										decorator: <ZTooltip text="Start date cannot be after end date"><ZIcon icon={ZIconType.Alert} variant="danger" /></ZTooltip>,
										position: "start",
									},
								],
							};
					}				
				},
			},
			{
				headerName: "End",
				field: "endDate",
				sortable: true,
				minWidth: 150,
				initialWidth: 150,
				cellEditor: "agDateCellEditor",
				valueSetter: (params) => {
					params.data.endDate = params.newValue ? datefns.format(params.newValue, "yyyy-MM-dd") : null;
					return true;
				},
				valueGetter: (params) => (params.data?.endDate == null ? null : new Date(params.data.endDate)),
				valueFormatter: (params) => (params.value ? datefns.format(new Date(params.value), "dd/MM/yyyy") : ""),
				editable: canEdit ? true : false,
				cellRenderer: "baseCellRenderer",
				cellRendererParams: (params: BaseColDefParams<CurrentModel>) => {
					if (params.data.hasError!["endDate"] === true) {
						return {
							decorators: [
									{
										decorator: <ZTooltip text="End date cannot be before start date"><ZIcon icon={ZIconType.Alert} variant="danger" /></ZTooltip>,
										position: "start",
									},
								],
							};
					}				
				},
			},
			// {
			// 	headerName: "Deal Type",
			// 	field: "dealType",
			// 	sortable: true,
			// 	minWidth: 80,
			// 	cellEditor: "agSelectCellEditor",
			// 	cellEditorParams: {
			// 		values: ["Fixed", "Percentage"],
			// 	},
			// 	valueGetter: (params) => (params.data === undefined ? "" : params.data.dealType === "C" ? "Fixed" : "Percentage"),
			// 	valueSetter: (params) => {
			// 		params.data.dealType = params.newValue === "Fixed" ? "C" : "P";
			// 		return true;
			// 	},
			// 	editable: canEdit ? true : false,
			// },

			{
				headerName: "Error", 
				field: "hasError",
				valueGetter: (params) => {
					const anyHasError = Object.values(params.data?.hasError ?? {}).some((v) => v === true);
					return anyHasError ? "Error" : "";
				},
				cellRenderer: "baseCellRenderer",
				cellRendererParams: (params: BaseColDefParams<CurrentModel>) => {
					const anyHasError = Object.values(params.data?.hasError ?? {}).some((v) => v === true);
					if (anyHasError) {

						return {
							decorators: [
									{
										decorator: <ZTooltip text="Row has errors, please correct to save."><ZIcon icon={ZIconType.Alert} variant="danger" /></ZTooltip>,
										position: "start",
									},
								],
							};
						}
				},
			}

		];

		//Fix the sorting number placement
		columns.forEach((col) => {
			col.headerComponentParams = {template: AgGridCustomColumn }
		});

		return columns;
	}, [suppliers, globalDiscountType]);


	const importWizardExtraMatches: CustomColumns[] = [
		{
			title: "MOQ",
			key: "Qty",
			required: true,
			display: true,
			validation: (value: any) => {
				if (value === "") {
					return { valid: false, error: "MOQ is required" };
				}
				if (isNaN(value)) {
					return { valid: false, error: "MOQ must be a number" };
				}
				if (parseInt(value) < 0) {
					return { valid: false, error: "MOQ must be a positive number" };
				}
				return { valid: true, error: "" };
			},
			autoMatch: ["qty"],
		},
		{
			title: globalDiscountType === "Fixed" ? "Dollar Discount" : "Percent Discount",
			key: "Value",
			required: true,
			display: true,
			validation: (value: any) => {
				if (value !== "" && isNaN(value)) {
					return { valid: false, error: "Value must be a number" };
				}
				if (value !== "" && parseFloat(value) < 0) {
					return { valid: false, error: "Value must be a positive number" };
				}
				return { valid: true, error: "" };
			},
		},
		{
			title: "Net into store cost",
			key: "ExpectedCost",
			required: false,
			display: true,
			validation: (value: any) => {
				if (value !== "" && isNaN(value)) {
					return { valid: false, error: "Value must be a number" };
				}
				if (value !== "" && parseFloat(value) < 0) {
					return { valid: false, error: "Value must be a positive number" };
				}
				return { valid: true, error: "" };
			},
		},
		{
			title: "Start Date",
			key: "StartDate",
			required: false,
			display: true,
			validation: (value: any) => {
				if (value === "") {
					return { valid: false, error: "Start Date is required" };
				}

				if (!datefns.isValid(datefns.parse(value, "P", new Date()))) {
					return { valid: false, error: "Invalid Date" };
				}

				return { valid: true, error: "" };
			},
			autoMatch: ["start"],
		},
		{
			title: "End Date",
			key: "EndDate",
			required: false,
			display: true,
			validation: (value: any) => {
				if (value === "") {
					return { valid: false, error: "End Date is required" };
				}

				if (!datefns.isValid(datefns.parse(value, "P", new Date()))) {
					return { valid: false, error: "Invalid Date" };
				}

				return { valid: true, error: "" };
			},
			autoMatch: ["end"],
		},
		{
			title: "Notes",
			key: "Notes",
			required: false,
			display: true,
			validation: (value: any) => {
				return { valid: true, error: "" };
			},
		},
	];

	function findSupplierId(supplierName: string) {
		//Check if its already a supplier code
		if (supplierName === undefined || supplierName === null || supplierName === "") return null;
		let supplierCodes: any = suppliers.find((item: any) => item.systemCode?.toLocaleLowerCase() === supplierName?.toLocaleLowerCase());
		if (supplierCodes) return supplierCodes.id;
		let supplier: any = suppliers.find((item: any) => item.name?.toLocaleLowerCase() === supplierName?.toLocaleLowerCase());
		if (supplier) return supplier.id;
		let supplierAltNames: any = supplierNameMappings.find((item: any) => item.alternateName?.toLocaleLowerCase() === supplierName?.toLocaleLowerCase());
		return supplierAltNames ? supplierAltNames.supplierId : null;
	}

	function WholeTableValidation(rows: CommonColumnProperties[]): CommonColumnProperties[] {
		let temp: CommonColumnProperties[] = [...rows];
		let IsValid = (text: any) => text !== undefined && text !== null && text !== "";

		const itemQtySet = new Set<string>();

		rows.forEach((row) => {
			if (row.hasError === undefined || row.hasError === false) {
				//don"t bother validating bad rows
				let errors: string[] = [];

				const checkSet = row.__pde + "|" + row.__apn + "|" + row.Qty;
				if (itemQtySet.has(checkSet)) {
					errors.push("Duplicate item and quantity");
				} else {
					itemQtySet.add(checkSet);
				}

				if (errors.length > 0) {
					row.hasError = true;
					row.validationState = ValidationState.Invalid;
					row.message = [...(row.message == null || row.message === "" ? [] : [row.message]), ...errors].join(", ");
				}
			}
		});
		return temp;
	}

	function WholeTablePostProcess(rows: CommonColumnProperties[]): CommonColumnProperties[] {
		let temp: CommonColumnProperties[] = [...rows];
		let IsValid = (text: any) => text !== undefined && text !== null && text !== "";

		const itemQtySet = new Set<string>();

		rows.forEach((row) => {
			if (row.hasError === undefined || row.hasError === false) {
				//don"t bother validating bad rows
				let errors: string[] = [];

				if (row.stockId != null && row.stockId !== "") {
					const checkSet = row.stockId + "|" + row.Qty;
					if (itemQtySet.has(checkSet)) {
						errors.push("Duplicate item and quantity");
					} else {
						itemQtySet.add(checkSet);
					}
				} else {
					errors.push("No Match");
				}

				if (errors.length > 0) {
					row.hasError = true;
					row.validationState = ValidationState.Invalid;
					row.message = [...(row.message == null || row.message === "" ? [] : [row.message]), ...errors].join(", ");
				}
			}
		});
		return temp;
	}

	const terminateWorkersRef = useRef<Function>();
	const [importPercentage, setImportPercentage] = useState<number>(0);
	async function importStockAsync(rows: any[]) {
		const webWorkerData: SupplierDealImportWorkerData = {
			data: rows,
			stockList: [...getAgGridRows()],
			suppliers: suppliers,
			defaultSupplier: undefined,
			globalDiscountType: globalDiscountType === "Fixed" ? "C" : "P",
			defaultStartDate: supplierDeal.startDate,
			defaultEndDate: supplierDeal.endDate,
		};

		pauseValidation.current = true;

		const errors: Map<string, string> = new Map<string, string>();

		const { runWorkers, terminateWorkers } = runBatchWorkers<SupplierDealImportWorkerData, any, SupplierDealImportWorkerResponseData>({
			workerData: webWorkerData,
			createWorker: () => new Worker(new URL("./supplierDealImportWorker.ts", import.meta.url), { type: "module" }),
			onWorkerResponse: async (event, responseContext, httpErrors, reject) => {
				// There's no http call in this web worker but should we keep this in case? Probably not... hmm...
				if (!responseContext.response.data) {
					if (httpErrors) {
						const combinedError = httpErrors.join(", ");
						// Now using this combined error, construct a { stockName, error } object for each stock and push it onto the errors array
						responseContext.originalBatchData.forEach((r: any) => {
							const errorEntry = errors.get(r.__name);
							errors.set(r.__name ?? "", errorEntry ? errorEntry + ", " + combinedError : combinedError);
						});
					}
				} else {
					const updatedStocks: CurrentModel[] = responseContext.response.data.updatedStocks.map((y) => {
						if (y.supplierId === 0 || y.supplierId == null) {
							return ({...y, customId: uuidv4(), hasError: {supplierId: true}});
						} else {
							return ({...y, customId: uuidv4(), hasError: {}});
						};
					});

					stockTableRef.current?.api.applyTransaction({
						add: updatedStocks,
					});
				}

				setImportPercentage((prev) => Math.min(100, prev + (1 / responseContext.numBatches) * 100));
			},
			onWorkersFinished: async (_) => {
				setImportPercentage(0)
			},
			onWorkerError: (error, unprocessedData) => {
				unprocessedData.forEach((data) => {
					const errorEntry = errors.get(data.__name);
					const errorMessage = error.message ?? "Unknown error occurred";
					errors.set(data.__name ?? "", errorEntry ? errorEntry + ", " + errorMessage : errorMessage);
				});
			},
		});

		terminateWorkersRef.current = terminateWorkers;
		await runWorkers;

		const returnErrors: { stockName: string; error: string }[] = [];

		errors.forEach((value: string, key: string) => {
			returnErrors.push({
				stockName: key,
				error: value,
			});
		});


		pauseValidation.current = false;
		agGridTableValidation();

		return [...returnErrors];
	}

	function handleSubmit() {
		setSaving(true);
		if (form.current?.checkValidity() === true) {
			console.log(supplierDeal);

			const sendModel:ManagedSupplierDealsTemplateView  = {
				...supplierDeal,
				stocks: getAgGridRows().map((s) => ({
					...s,
					managedSupplierDealId: supplierDeal.id,
					dealType: globalDiscountType === "Fixed" ? "C" : "P",
				})),
			}

			if (isNew) {
				ManagedSupplierDealsApi.Add([sendModel]).then((res) => {
					if (res.data.data.length > 0) {
						navigate("/app/supplier-stock-deals/" + res.data.data[0]);
					} else {
						dispatch(showToast({ value: ["Failed to add Stock Deal"] }));
					}

					setSaving(false);
				}).catch(() => setSaving(false));
			} else {
				ManagedSupplierDealsApi.Update([sendModel]).then((res) => {
					if (res.data.data) {
						navigate("/app/supplier-stock-deals/" + modelId);
					} else {
						dispatch(showToast({ value: ["Failed to update Stock Deal"] }));
					}

					setSaving(false);
				}).catch(() => setSaving(false));
			}
		}

		setShowConfirm(false);
		setValidated();
	}

	function addStock() {
		if(stocks.selectAllFilter != true){
			const selectedIds = stocks.selectedRows[0].map(x => x.id);
			const stocksToAdd = stocks.table.filter(x=> selectedIds.includes(x.id));
			processAddedManageStockData(stocksToAdd);
			setShowAdd(false);
			return;
		}

		//Load all managed stock
		ManagedStocksApi.OptimsedSearch({
			keyword: stocks.keyword,
			searchPdes: stocks.searchPdes,
			searchBarcodes: stocks.searchBarcodes,
			offset: null,
			limit: null,
			supplierIds: stocks.supplierIdFilter == undefined? []: [stocks.supplierIdFilter],
			departmentIds: stocks.departmentIdFilter == undefined? []: [stocks.departmentIdFilter],
			subdepartmentIds: stocks.subdepartmentIdFilter == undefined? []: [stocks.subdepartmentIdFilter],
			manufacturerIds: stocks.manufacturerIdFilter == undefined? []: [stocks.manufacturerIdFilter],
			stockGroupIds: stocks.stockGroupIdFilter == undefined? []: [stocks.stockGroupIdFilter],
			active: stocks.activeFilter,
			ethical: stocks.ethicalFilter,
			schedule: stocks.scheduleFilter,
			pbs: stocks.pbsFilter
		})
		.then((res) => {
			processAddedManageStockData(res.data.data);
		})
		.finally(() => {
			setShowAdd(false);
		});
	}

	function processAddedManageStockData(managedStock: any[]){
		const newStocks: CurrentModel[] = managedStock.map((r) => ({
			id: r.id,
			name: r.name,
			supplierName: "",
			startDate: supplierDeal.startDate || null,
			endDate: supplierDeal.endDate || null,
			qty: 1,
			value: 0,
			dealType: globalDiscountType === "Fixed" ? "C" : "P",
			notes: "",
			managedStockId: r.id,
			managedSupplierDealId: 0,
			supplierId: 0,
			costEx: r.costEx,
			expectedCost: null,
			hasError: {},
			customId: uuidv4(),
		}));

		stockTableRef.current?.api.applyTransaction({
			add: newStocks,
		});
		agGridTableValidation();
	}

	function getAgGridRows(): CurrentModel[] {
		const rows: CurrentModel[] = [];
		if(stockTableRef.current?.api){
			stockTableRef.current.api.forEachNode((node) => rows.push(node.data));
		}
		return rows;
	}

	const runningValidation = useRef(false);
	const pauseValidation = useRef(false);
	function agGridTableValidation() {

		if (runningValidation.current)
			return;

		if (pauseValidation.current)
			return;

		runningValidation.current = true;

		console.log("agGridTableValidation")
		var hasChange = false;
		var hasError = false;


		if (supplierDeal.name === "") {
			hasError = true;
		}

		if (supplierDeal.endDate != null && supplierDeal.startDate != null && datefns.isAfter(new Date(supplierDeal.startDate), new Date(supplierDeal.endDate))) {
			hasError = true;
		}

		
		const key = (row: CurrentModel) => row.managedStockId + "|" + row.supplierId + "|" + row.qty;
		const stocks: CurrentModel[] = getAgGridRows();
		setTotalRows(stocks.length);

		const groupedStocks = stocks.reduce((acc, stock) => {
			const stockKey = key(stock);
			if (!acc[stockKey]) {
				acc[stockKey] = [];
			}
			acc[stockKey].push(stock);
			return acc;
		}, {} as { [key: string]: CurrentModel[] });

		stocks.forEach((stock: CurrentModel) => {
			hasChange = false;
			const isDuplicate = groupedStocks[key(stock)]?.some((x: CurrentModel) => x.customId !== stock.customId) ?? false;
			if (isDuplicate !== (stock.hasError?.name === true)) {
				stock.hasError = { ...stock.hasError, name: isDuplicate };
				hasChange = true;
			}

			if ((stock.supplierId === 0 || stock.supplierId == null) && (stock.hasError?.supplierId == null || stock.hasError?.supplierId === false)) {
				stock.hasError = { ...stock.hasError, supplierId: true };
				hasChange = true;
			} else if (stock.hasError!["supplierId"] === true && stock.supplierId !== 0 && stock.supplierId != null) {
				stock.hasError = { ...stock.hasError, supplierId: false };
				hasChange = true;
			}

			if ((stock.qty < 0 || isNaN(stock.qty) || stock.qty == null) && (stock.hasError?.qty == null || stock.hasError?.qty === false)) {
				stock.hasError = { ...stock.hasError, qty: true };
				hasChange = true;
			} else if (stock.hasError!["qty"] === true && stock.qty != null && stock.qty >= 0 && !isNaN(stock.qty)) {
				stock.hasError = { ...stock.hasError, qty: false };
				hasChange = true;
			}


			if (stock.startDate != null && stock.endDate != null && datefns.isAfter(new Date(stock.startDate), new Date(stock.endDate)) && (stock.hasError?.startDate == null || stock.hasError?.startDate === false || stock.hasError?.endDate == null || stock.hasError?.endDate === false)) {
				stock.hasError = { ...stock.hasError, startDate: true };
				stock.hasError = { ...stock.hasError, endDate: true };
				hasChange = true;
			} else if ((stock.hasError!["startDate"] === true || stock.hasError!["endDate"] === true) && (stock.startDate == null || stock.endDate == null || datefns.isBefore(new Date(stock.startDate), new Date(stock.endDate)))) {
				stock.hasError = { ...stock.hasError, startDate: false };
				stock.hasError = { ...stock.hasError, endDate: false };
				hasChange = true;
			}


			hasError = hasError || (stock.hasError?.name === true || stock.hasError?.supplierId  === true || stock.hasError?.qty  === true)

			if (hasChange) {
				const node = stockTableRef.current?.api.getRowNode(stock.customId ?? "");
				if (node) {
					node.setData({ ...stock});
				}
			}
		});

		setHasErrors(hasError);

		runningValidation.current = false;
	}

	useEffect(() => {
		agGridTableValidation();
	}, [supplierDeal]);

	return (
		<>
			<Title pageTitle={isNew ? "New Stock Deal" : "Update Stock Deal"} crumbs={[{ name: "Stock Deals", path: "/app/supplier-stock-deals" }]} />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Col>
									<Form onSubmit={handleSubmit} validated={isValidated} ref={form}>
										<Row>
											<Col md={4}>
												<ZTextInput
													label="Deal Name:"
													type="text"
													placeholder="Enter stock deal name"
													value={supplierDeal.name}
													key="text"
													onChange={(e) =>
														setSupplierDeal((prev) => ({
															...prev,
															name: e,
														}))
													}
													validation={{
														invalid: supplierDeal.name === "",
														error: "Name is required",
													}}
													disabled={!canEdit}
												/>
											</Col>

											<Col md={4}>
												<ZSingleSelectDropdown
													id="discountType"
													placeholder="Select Discount Type"
													label={<ZLabel>Discount Type:</ZLabel>}
													options={["Fixed", "Percentage"]}
													disabled={() => false}
													display={(opt) => opt}
													onSelectionChange={(model) => {
														if (model) {
															setGlobalDiscountType(model);
															stockTableRef.current?.api.forEachNode((node) => {
																node.data.dealType = model === "Fixed" ? "C" : "P";
															});
														}
													}}
													searchPlaceholder="Select Discount Type"
													optionKey={(option) => {
														return option;
													}}
													value={globalDiscountType}
													dropdownIsDisabled={!canEdit}
												/>
											</Col>

											<Col md={4}>
												<ZMultiSelectDropdown<StoreExtended>
													containerClassName="w-100"
													label="Stores:"
													placeholder="Select stores"
													values={stores.filter((s) => supplierDeal.stores.includes(s.id))}
													options={stores}
													display={(s) => s.name}
													value={(s) => s.id}
													minMenuWidthPx={300}
													disabled={(s) => false}
													onSelectionChange={(opt) => setSupplierDeal((prev) => ({ ...prev, stores: opt.map((o) => o.id) }))}
													hasAllOption
													hasSearch
													searchPlaceholder="Search stores"
													dropdownIsDisabled={!canEdit}
												/>
											</Col>
										</Row>

										<Row className="mt-1">
											
											<Col md={4}>
												<ZTextInput
													label="Deal Notes:"
													type="text"
													placeholder="Enter deal notes"
													value={supplierDeal.notes ?? ""}
													key="text"
													onChange={(e) =>
														setSupplierDeal((prev) => ({
															...prev,
															notes: e,
														}))
													}
													disabled={!canEdit}
												/>
											</Col>

											<Col md={4}>
												<ZDateTimePicker
													label="Start Date"
													value={supplierDeal.startDate != null ? new Date(supplierDeal.startDate) : null}
													onChanged={(date) => {
														setSupplierDeal((prev) => ({
															...prev,
															startDate: date != null ? datefns.format(date, "yyyy-MM-dd") : null,
														}));
													}}
													disabled={!canEdit}
													error={supplierDeal.startDate != null && supplierDeal.endDate != null && datefns.isAfter(new Date(supplierDeal.startDate), new Date(supplierDeal.endDate)) ? "Start date cannot be after end date" : undefined}
												/>
											</Col>
											<Col md={4}>
												<ZDateTimePicker
													label="End Date"
													value={supplierDeal.endDate != null ? new Date(supplierDeal.endDate) : null}
													onChanged={(date) => {
														setSupplierDeal((prev) => ({
															...prev,
															endDate: date != null ? datefns.format(date, "yyyy-MM-dd") : null,
														}));
													}}
													disabled={!canEdit}
													error={supplierDeal.startDate != null && supplierDeal.endDate != null && datefns.isAfter(new Date(supplierDeal.startDate), new Date(supplierDeal.endDate)) ? "End date cannot be before start date" : undefined}
												/>
											</Col>
										</Row>

										<AgGridToolbar 
												search={true}
												className="my-1 px-2 pt-2"
												handleSubmit={(searchState, filterState) => {
													gridSearch.current = { searchState, filterState };
													console.log(stockTableRef.current?.api.getFilterModel());
													if (searchState?.keyword === "") {
														stockTableRef.current?.api.setFilterModel({});
														stockTableRef.current?.api.onFilterChanged();
													} else {
														stockTableRef.current?.api.setFilterModel({
															name: {
																filterType: "text",
																type: searchState?.contains === true ?  "contains" : "startsWith",
																filter: searchState?.keyword ?? ""
															}
														});
														stockTableRef.current?.api.onFilterChanged();
													}
												}}
												searchProps={
													{totalRows: totalRows, placeholder: "by Name", hideContains: false}
												}
												children={(<div className="d-inline-flex gap-1">
				
				
													<ZTooltip enabled={canEdit} text={canEdit ? "Add" : "You do not have permission to add"}>
														<ZButton buttonSize="md" icon={ZIconType.Add} variant="light" onClick={() => setShowAdd(true)} 
															className={!canEdit ? "invisible" : ""}/>
													</ZTooltip>

													<ZTooltip enabled={canEdit} text="Copy">
														<ZButton
															buttonSize="md"
															icon={ZIconType.Copy}
															disabled={selected.length === 0}
															className={!canEdit ? "invisible" : ""}
															variant="light"
															onClick={() => {
																//get all the selected rows
																const selectedRows = stockTableRef.current?.api.getSelectedRows();
																//duplicate them
																if (selectedRows) {
																	const newRows = selectedRows.map((r: CurrentModel) => ({ ...r, customId: uuidv4(), hasError: {...r.hasError, name: true} }));
																	//add them to the table
																	stockTableRef.current?.api.applyTransaction({
																		add: newRows,
																	});
																}
															}}
															
														/>
													</ZTooltip>

													<ZTooltip enabled={canEdit} text={canEdit ? "Bulk Edit" : "You do not have permission to bulk edit"}>
														<ZButton
															buttonSize="md"
															icon={ZIconType.Edit}
															disabled={selected.length === 0}
															className={!canEdit ? "invisible" : ""}
															variant="light"
															onClick={() => {
																setShowBulkEdit(true);
															}}
														/>
													</ZTooltip>


													<ZTooltip enabled={canEdit} text={canEdit ? "Delete" : "You do not have permission to delete"}>
														<ZButton
															buttonSize="md"
															icon={ZIconType.Delete}
															disabled={selected.length === 0}
															className={!canEdit ? "invisible" : ""}
															variant="light"
															onClick={() => {
																//Remove selected rows
																const selectedRows = stockTableRef.current?.api.getSelectedRows();
																if (selectedRows) {
																	stockTableRef.current?.api.applyTransaction({
																		remove: selectedRows,
																	});
																}
															}}
														/>
													</ZTooltip>
				
													<ZTooltip enabled={canEdit} text={canEdit && user.permissions.supplierDeals.canImport ? "Import" : "You do not have permission to import"}>
														<ZButton
															buttonSize="md"
															icon={ZIconType.Upload}
															variant="light"
															onClick={() => {
																setShowImport(true);
															}}
															className={!user.permissions.supplierDeals.canImport ? "invisible" : ""}
														/>
													</ZTooltip>
				
												</div>
													
												)}
											/> 

										<Row>
											<AgGridReact<CurrentModel>
												ref={stockTableRef}
												columnDefs={stockColumns}
												rowData={supplierDealStocks}
												getRowId={(p) => p.data.customId ?? ""}
												rowSelection="multiple"
												className={`ag-theme-quartz ag-theme-custom w-100 flex-grow`}
												containerStyle={{ height: "60dvh" }}
												suppressRowClickSelection
												singleClickEdit
												rowMultiSelectWithClick
												groupAllowUnbalanced
												animateRows
												suppressContextMenu
												editType="fullRow"
												stopEditingWhenCellsLoseFocus={true}
												onSelectionChanged={(e) => {
													setSelected(e.api.getSelectedRows().map((r: CurrentModel) => r.managedStockId));
												}}
												enterNavigatesVertically
												enterNavigatesVerticallyAfterEdit
												gridOptions={{
													rowClassRules: {
														"ag-row-error": (params) => {
															const anyHasError = Object.values(params.data?.hasError ?? {}).some((v) => v === true);
															return anyHasError;
														}
													},
												}}
												components={{
													baseCellRenderer: BaseCellRenderer,
												}}
												onRowDataUpdated={() => {
													agGridTableValidation();
												}}
												onRowValueChanged={() => {
													agGridTableValidation();
												}}
											/>
										</Row>

										<Row className="mt-2">
											<Col sm={6}>
												<Link to="/app/supplier-stock-deals" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
													<i className="mdi mdi-arrow-left"></i> Back to Stock Deals
												</Link>
											</Col>
											<Col sm={6} className="d-flex flex-row-reverse">
												<ZTooltip text={hasErrors && canEdit ? "Please ensure all fields are valid before saving" : (
																!canEdit ? "You do not have permission to edit" : "") }
																enabled={hasErrors || !canEdit}>	
													<ZButton
														variant="primary"
														className="btn cta-button"
														disabled={saving || loading || hasErrors || !canEdit} 
														onClick={() => {
															if (form.current?.checkValidity() === true) {
																stockTableRef.current?.api.stopEditing();
																setShowConfirm(true);
															}
			
														}}
													>
														{saving ? (
															<>
																<Spinner className="spinner-border-sm me-1" tag="span" color="white" />
																Saving...
															</>
														) : (
															<>Save</>
														)}
													</ZButton>
												</ZTooltip>
											</Col>
										</Row>
									</Form>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			<BSModal
				centered
				size={"sm"}
				show={loading}
				keyboard={false}
				animation={true}
				backdrop={"static"}
				
			>
				<BSModal.Dialog className="m-0">
					<BSModal.Body>
						<div style={{textAlign: 'center'}}>Loading</div>
					</BSModal.Body>
				</BSModal.Dialog>
			</BSModal>

			<ManagedStocksPopup show={showAdd} onClose={() => setShowAdd(false)} onSave={addStock} />

			<ImportWizard
				ref={importWizardRef}
				title="Stock Deals"
				display={showImport}
				matchAgainst={"Stocks"}
				customColumns={importWizardExtraMatches}
				validateTable={(rows) => WholeTableValidation(rows)}
				postProcessStep={(rows) => WholeTablePostProcess(rows)}
				finish={() => setShowImport(false)}
				importPercentage={importPercentage}
				handleClose={() => {
					if (terminateWorkersRef.current) terminateWorkersRef.current();
					setShowImport(false);
				}}
				importAsync={(i) => importStockAsync(i)}
			/>

			<Modal
				title="Confirm"
				show={showConfirm}
				buttonType="primary"
				buttonText={"Continue"}
				buttonCloseText="Cancel"
				handleSubmit={handleSubmit}
				handleClose={() => setShowConfirm(false)}
				description={
					<>
						<p>You are about to sync the Stock Deal down to the selected store(s).</p>
						<p>Are you sure you want to continue?</p>
					</>
				}
			/>

			<Modal
				title="Bulk Edit"
				show={showBulkEdit}
				buttonType="primary"
				buttonText={"Continue"}
				buttonCloseText="Cancel"
				handleSubmit={() => {
					const selectedRows = stockTableRef.current?.api.getSelectedRows();
					if (selectedRows) {
						selectedRows.forEach((r) => {
							if (bulkOptions.supplierId != null) r.supplierId = bulkOptions.supplierId;
							if (bulkOptions.startDate != null) r.startDate = bulkOptions.startDate;
							if (bulkOptions.endDate != null) r.endDate = bulkOptions.endDate;
							if (bulkOptions.qty != null) r.qty = bulkOptions.qty;
							if (bulkOptions.value != null) r.value = bulkOptions.value;
							if (bulkOptions.dealType != null) r.dealType = bulkOptions.dealType;
							if (bulkOptions.notes != null) r.notes = bulkOptions.notes;
						});
					}

					agGridTableValidation();
					stockTableRef.current?.api.refreshCells();

					setShowBulkEdit(false);
					setBulkOptions({
						supplierId: null,
						startDate: null,
						endDate: null,
						qty: null,
						value: null,
						dealType: null,
						notes: null,
					});
				}}
				handleClose={() => setShowBulkEdit(false)}
				description={
					<>
						<Row>
							<Col sm={12}>
								<ZSingleSelectDropdown<ManagedSupplier>
									containerClassName="w-100"
									label="Supplier:"
									placeholder="Select Supplier"
									options={suppliers}
									value={suppliers.find((s) => s.id === bulkOptions.supplierId)}
									display={(s) => s.name}
									disabled={(s) => false}
									onSelectionChange={(opt) => setBulkOptions((prev) => ({ ...prev, supplierId: opt?.id ?? null }))}
									hasSearch
									searchPlaceholder="Search Suppliers"
									optionKey={function (option: ManagedSupplier) {
										return option.id;
									}}
								/>
							</Col>
						</Row>
						<Row>
							<Col sm={12}>
								<ZDateTimePicker
									label="Start Date"
									value={bulkOptions.startDate != null ? new Date(bulkOptions.startDate) : null}
									onChanged={(date) => {
										setBulkOptions((prev) => ({
											...prev,
											startDate: date != null ? datefns.format(date, "yyyy-MM-dd") : null,
										}));
									}}
								/>
							</Col>
						</Row>
						<Row>
							<Col sm={12}>
								<ZDateTimePicker
									label="End Date"
									value={bulkOptions.endDate != null ? new Date(bulkOptions.endDate) : null}
									onChanged={(date) => {
										setBulkOptions((prev) => ({
											...prev,
											endDate: date != null ? datefns.format(date, "yyyy-MM-dd") : null,
										}));
									}}
								/>
							</Col>
						</Row>
						<Row>
							<Col sm={12}>
								<ZNumberInput
									label="MOQ:"
									placeholder="Enter MOQ"
									value={bulkOptions.qty}
									key="text"
									onChange={(e) =>
										setBulkOptions((prev) => ({
											...prev,
											qty: e === undefined ? null : e,
										}))
									}
									type={"qty"}
								/>
							</Col>
						</Row>
						<Row>
							<Col sm={12}>
								<ZNumberInput
									label={globalDiscountType === "Fixed" ? "Dollar Discount:" : "Percent Discount:"}
									placeholder="Enter Value"
									value={bulkOptions.value}
									key="text"
									onChange={(e) =>
										setBulkOptions((prev) => ({
											...prev,
											value: e === undefined ? null : e,
										}))
									}
									type={"decimal"}
								/>
							</Col>
						</Row>
						<Row>
							<Col sm={12}>
								<ZTextInput
									label="Notes:"
									type="text"
									placeholder="Enter notes"
									value={bulkOptions.notes ?? ""}
									key="text"
									onChange={(e) =>
										setBulkOptions((prev) => ({
											...prev,
											notes: e,
										}))
									}
								/>
							</Col>
						</Row>
					</>
				}
			/>
		</>
	);
}
